import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FEATURES, hasFeature } from '@/features/feature-flags';
import { runFrames } from '@/lib/animate';

import type { TranscriptDisplayLine } from './meetings-transcript.utils';
import type {
  CalendarEventAttendee,
  MeetingAudioEnergyLevel,
} from './meetings.types';

const OTHER_SPEAKER_LABELS = new Set(['Other', 'Others']);

const INITIAL_JUMP_FRAMES = 10;

export function useProcessedTranscripts(
  transcripts: TranscriptDisplayLine[],
  attendees?: CalendarEventAttendee[] | null,
  resolvedSpeakerNames?: ReadonlyMap<string, string>
) {
  const hideOtherSpeakerLabels = hasFeature(
    FEATURES.REALTIME_SPEAKER_ATTRIBUTION
  );

  return useMemo(() => {
    return transcripts
      .map((line, lineIndex) => {
        const resolvedName = line.speaker
          ? resolvedSpeakerNames?.get(line.speaker)
          : undefined;
        const isOtherSpeakerLabel =
          !!line.speaker && OTHER_SPEAKER_LABELS.has(line.speaker);
        const speaker =
          resolvedName ??
          (hideOtherSpeakerLabels && isOtherSpeakerLabel
            ? null
            : resolveTranscriptSpeakerLabel(line.speaker, attendees));
        return {
          sourceLineIndex: lineIndex,
          speaker,
          rawSpeaker: line.speaker,
          segments: line.segments,
          isOthers: line.speaker ? line.speaker !== 'You' : false,
        };
      })
      .filter((item) =>
        item.segments.some((segment) => segment.text.trim().length)
      );
  }, [attendees, transcripts, hideOtherSpeakerLabels, resolvedSpeakerNames]);
}

export function useAudioEnergyPulse(
  subscribeEnergyLevel: (
    cb: (levels: MeetingAudioEnergyLevel) => void
  ) => () => void
) {
  const elementRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let targetEnergy = 0;
    let displayedEnergy = 0;
    let rafId: number | null = null;

    const step = () => {
      rafId = null;
      displayedEnergy += (targetEnergy - displayedEnergy) * 0.12;
      if (Math.abs(targetEnergy - displayedEnergy) < 0.005) {
        displayedEnergy = targetEnergy;
      }
      elementRef.current?.style.setProperty(
        '--live-energy',
        displayedEnergy.toFixed(3)
      );
      if (displayedEnergy !== targetEnergy) {
        rafId = requestAnimationFrame(step);
      }
    };

    const unsubscribe = subscribeEnergyLevel((levels) => {
      const meanLevel =
        levels.reduce((sum, level) => sum + level, 0) / (levels.length || 1);
      targetEnergy = Math.min(1, meanLevel);
      if (rafId === null) rafId = requestAnimationFrame(step);
    });

    return () => {
      unsubscribe();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [subscribeEnergyLevel]);

  return elementRef;
}

export function useJumpToLive(lineCount: number) {
  const [isScrollPaused, setIsScrollPaused] = useState(false);
  const lineCountRef = useRef(lineCount);
  lineCountRef.current = lineCount;
  const pausedLineCountRef = useRef(0);
  const seenLineCountRef = useRef(lineCount);

  const handlePausedChange = useCallback((paused: boolean) => {
    if (paused) pausedLineCountRef.current = lineCountRef.current;
    setIsScrollPaused(paused);
  }, []);

  useEffect(() => {
    seenLineCountRef.current = Math.max(seenLineCountRef.current, lineCount);
  }, [lineCount]);

  return {
    isScrollPaused,
    handlePausedChange,
    isLineNew: (index: number) => index >= seenLineCountRef.current,
    newLineCount: Math.max(0, lineCount - pausedLineCountRef.current),
  };
}

export function useInitialJumpToLive({
  enabled,
  onJumpFrame,
}: {
  enabled: boolean;
  onJumpFrame: () => void;
}) {
  const [isJumpReady, setIsJumpReady] = useState(false);
  const onJumpFrameRef = useRef(onJumpFrame);
  onJumpFrameRef.current = onJumpFrame;

  useEffect(() => {
    if (!enabled) return;
    return runFrames(
      INITIAL_JUMP_FRAMES,
      () => onJumpFrameRef.current(),
      () => setIsJumpReady(true)
    );
  }, [enabled]);

  return isJumpReady;
}

function resolveTranscriptSpeakerLabel(
  speaker: string | null,
  attendees?: CalendarEventAttendee[] | null
): string | null {
  if (!speaker || !OTHER_SPEAKER_LABELS.has(speaker)) return speaker;
  return getOneOnOneAttendeeLabel(attendees) ?? speaker;
}

function getOneOnOneAttendeeLabel(
  attendees?: CalendarEventAttendee[] | null
): string | null {
  if (!attendees?.length) return null;

  const otherAttendees = attendees.filter(
    (attendee) => !attendee.self && !attendee.resource
  );
  if (otherAttendees.length !== 1) return null;

  return getAttendeeDisplayName(otherAttendees[0]);
}

export function getAttendeeDisplayName(
  attendee: CalendarEventAttendee
): string | null {
  const displayName = attendee.displayName?.trim();
  if (displayName) return displayName;

  const email = attendee.email?.trim();
  if (!email) return null;

  return formatEmailLocalPart(email);
}

function formatEmailLocalPart(email: string): string {
  const localPart = email.split('@', 1)[0].trim();
  if (!localPart) return email;
  return localPart
    .split(/[._-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
